import React, { useState } from 'react';
import Hero from '../../components/Hero/Hero';
import Pathfinder from '../../components/Pathfinder/Pathfinder';
import Section from '../../components/Section/Section';
import Card from '../../components/Card/Card';
import GlobalCallout from '../../components/Global/CalloutDefault';
import Wallpaper from '../../components/Wallpaper/Wallpaper';
import { Box, Typography } from '@mui/material';
import Accordion from '../../components/Accordion/Accordion';
import Metadata from '../../components/Metadata/Metadata';
import Button from '../../components/Button/Button';
import Image from '../../components/Image/Image';
import ContentSplit from '../../components/ContentSplit/ContentSplit';
import ListItem from '../../components/ListItem/ListItem';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="Innovation & R&D at General Genomics"
        description="Explore General Genomics’ cutting-edge R&D in bioscience and AI. Learn about pioneering projects, patented methodologies, and their impact on healthcare, biosecurity, and more."
      />
      <Hero
        helper="Innovation research and development"
        headline="Innovation and research at the forefront of bioinformatics"
      />

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Revolutionize Your Bioscience and Digital Systems Approach
        </h2>
        <p className="action-on-view action-fade-in">
          Discover how General Genomics is transforming bioscience and AI by
          delivering solutions that drive productivity, enhance efficiency, and
          optimize outcomes. Through groundbreaking research and development, we
          leverage advanced algorithms, large language models (LLMs),
          explainable AI (XAI), and cutting-edge technologies to address
          challenges in bioinformatics, biosciences, cyber systems, and digital
          ecosystems. From predictive analytics to genomic research, our
          innovations streamline complex processes, unlock operational
          efficiencies, and convert unstructured data into actionable insights.
          These advancements empower industries such as healthcare, national
          security, aviation, and supply chain risk management (SCRM) to achieve
          unparalleled precision and performance. Contact us today to optimize
          your digital systems, elevate productivity, and conquer your most
          complex challenges.
        </p>
        <Image
          path="/images/library/office-startup-9.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit
        image="/images/library/military-meeting-5.png"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">Discover More</p>
        <h2 className="action-on-view action-fade-in">
          Explore the Power of Innovation
        </h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Pioneering R&D Projects:</strong> Dive into AI-driven tools
            like Curo46™ and genomic breakthroughs shaping the future of
            healthcare and security.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Proprietary Methodologies:</strong> Learn about our
            published and pending patents.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Client-Focused Use Cases:</strong> See how our advancements
            drive success in disease detection, biosecurity, and data-powered
            tools and decisions (e.g., algorithms, LLMs).
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Real-Time Progress Insights:</strong> Explore research
            milestones through dynamic infographics and success stories from our
            leading edge projects, products, and solutions.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore More About General Genomics
        </h2>
        <p className="action-on-view action-fade-in">
          Dive deeper into General Genomics’ offerings and how our cutting-edge
          R&D is shaping industries and solutions worldwide.
        </p>
        <div className="card-group">
          <Card
            icon="science"
            headline="Curo46™: AI at the Forefront"
            subhead="Explore our flagship AI solution and how it’s revolutionizing genomic research."
            btnURL="/innovation/curo46"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="data_usage"
            headline="Synthetic & De-Identified Data"
            subhead="Learn how we leverage synthetic and de-identified data for advanced research capabilities."
            btnURL="/data-portal"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="business_center"
            headline="Government Public Health"
            subhead="Discover how our R&D supports public health efforts and enhances data-driven strategies."
            btnURL="/industries/government/public-health-and-policy"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../images/library/architecture-building-2.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about General Genomics’ Innovation and R&D
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Discover how General Genomics leverages bioscience and AI to drive
          advancements in healthcare, biosecurity, and predictive analytics.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What is General Genomics’ approach to innovation in bioscience, biosecurity, and AI?"
          >
            General Genomics focuses on cutting-edge research, AI-driven
            solutions like <Link to="/innovation/curo46">Curo46™</Link>, and
            predictive analytics to address complex challenges in healthcare,
            biosecurity, and more.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How do General Genomics’ R&D efforts benefit clients?"
          >
            Our advancements in disease detection, biosecurity, and data-driven
            decision-making deliver tailored solutions that empower clients
            across industries.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What proprietary technologies does General Genomics offer?"
          >
            We develop patented methodologies and solutions like{' '}
            <Link to="/innovation/curo46">Curo46™</Link> and synthetic data
            (SYN46™) tools that set new standards in bioscience and AI
            innovation.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Which industries benefit from General Genomics’ research and development?"
          >
            Our innovations support critical sectors, including healthcare,
            government{' '}
            <Link to="/industries/government/public-health-and-policy">
              public health
            </Link>{' '}
            and{' '}
            <Link to="/industries/government/national-security">
              national security
            </Link>
            , by addressing unique industry challenges.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
